/* A container for content wider than the column it sits in.
 *
 * BRIEF.md, step 16: "The reconciliation table and every wide table get their
 * own horizontally scrollable container with a visible scroll affordance.
 * Never overflow the page." Three places needed one and only the extracted
 * table had it — so the measuring, the edge marks and the line of text that
 * says what is off screen live here, once, and the four wide blocks in the
 * application use them.
 *
 * The affordance is three things, because one is not enough on its own:
 *
 *   - an edge mark, drawn only on the side that has more content on it, so a
 *     reader can see at a glance that the frame continues;
 *   - the frame itself is a focusable region with a name, so the arrow keys
 *     reach the rest of it without a mouse and a screen reader announces that
 *     it scrolls;
 *   - a line of text under it saying so, because an edge shadow is colour and
 *     colour is never the only cue in this system.
 *
 * overflow-y is explicitly hidden rather than left to compute to `auto`: a
 * frame that becomes a vertical scroll container with nothing to scroll
 * swallows every wheel gesture made over it. The one caller that does want a
 * vertical scroll — a long receipt — says so with its own class.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import './ScrollFrame.css';

export interface ScrollFrameProps {
  /**
   * What the region is called. Announced when the frame takes focus, so it
   * must say what is inside and that it scrolls.
   */
  label: string;
  /**
   * The line shown under the frame when — and only when — there is something
   * off screen. Omit where the caller draws its own.
   */
  hint?: ReactNode;
  children: ReactNode;
  /** Applied to the outer element, which is what the edge marks are drawn on. */
  className?: string;
  /** Applied to the scrolling element, for a caller that owns its frame. */
  frameClassName?: string;
}

export default function ScrollFrame({
  label,
  hint,
  children,
  className,
  frameClassName,
}: ScrollFrameProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({
    scrollable: false,
    fromLeft: false,
    toRight: false,
  });

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const measure = () => {
      const slack = frame.scrollWidth - frame.clientWidth;
      setState({
        scrollable: slack > 1,
        fromLeft: frame.scrollLeft > 1,
        toRight: slack > 1 && frame.scrollLeft < slack - 1,
      });
    };
    measure();
    frame.addEventListener('scroll', measure, { passive: true });
    // Re-measured on resize as well as on scroll: the same table is inside a
    // 24rem pane at one width and the full column at another.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(frame);
    return () => {
      frame.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, []);

  const classes = [
    'sfr',
    state.fromLeft ? 'sfr--from-left' : null,
    state.toRight ? 'sfr--to-right' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {/* The edge marks are drawn against this box rather than the outer one,
          so they cover the frame and stop at it — a mark over the line of text
          below would read as a smudge rather than as an edge. */}
      <div className="sfr__shell">
        <div
          ref={frameRef}
          className={['sfr__frame', frameClassName].filter(Boolean).join(' ')}
          role="region"
          tabIndex={0}
          aria-label={label}
        >
          {children}
        </div>
      </div>
      {state.scrollable && hint ? <p className="sfr__hint">{hint}</p> : null}
    </div>
  );
}
